import React, { useState, useRef } from "react";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { parseCourseExcelFile, generateCourseExcelTemplate } from "../../../utils/generateCourseExcelTemplate";
import { CreateCoursePlaceholderMutation } from "../../../graphql/generated/graphql";
import BulkUploadCourseModal, {
  BulkUploadModalState,
  CourseUploadResult,
} from "../../../components/settings/BulkUploadCourseModal/BulkUploadCourseModal";

interface BulkUploadCourseModalContainerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<CreateCoursePlaceholderMutation>;
  onGenerateCourse: (courseId: string, numberOfModules: number, numberOfActivities: number) => Promise<any>;
}

const BulkUploadCourseModalContainer: React.FC<BulkUploadCourseModalContainerProps> = ({
  visible,
  onClose,
  onSubmit,
  onGenerateCourse,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: BulkUploadModalState.CLOSED,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResults, setUploadResults] = useState<CourseUploadResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    onClose();
    setSelectedFile(null);
    setUploadResults([]);
    setIsProcessing(false);
    setProcessingStatus("");
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const buffer = generateCourseExcelTemplate();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "course_upload_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating template:", error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsProcessing(true);
      setProcessingStatus("Parsing Excel file...");
      setUploadResults([]);

      const parseResult = await parseCourseExcelFile(selectedFile);

      // Check for validation errors first
      if (parseResult.validationErrors.length > 0) {
        // Convert validation errors to upload results
        const validationResults: CourseUploadResult[] = parseResult.invalidCourses.map((course) => ({
          courseName: course.courseName || `Unknown course`,
          success: false,
          error: course.errors?.map((e) => e.message).join(", ") || "Validation failed",
        }));

        setUploadResults(validationResults);
        setIsProcessing(false);
        setProcessingStatus("");
        setModalState({ state: BulkUploadModalState.UPLOAD_ERROR });
        return;
      }

      // Process only valid courses
      const results: CourseUploadResult[] = [];
      const validCourses = parseResult.validCourses;

      for (let i = 0; i < validCourses.length; i++) {
        const course = validCourses[i];
        setProcessingStatus(`Processing course ${i + 1} of ${validCourses.length}: ${course.courseName}`);

        try {
          // Create course
          const response = await onSubmit({
            courseName: course.courseName,
            courseType: course.courseType,
            description: course.description,
            objectives: course.objectives,
            targetAudience: course.targetAudience,
            cost: course.cost,
            degrees: course.degrees,
          });

          const result = response?.createCoursePlaceholder;

          if (result?.success && result.data && result.data.length > 0) {
            // Generate course content
            const courseId = result.data[0].id;
            setProcessingStatus(`Generating content for course: ${course.courseName}`);

            await onGenerateCourse(courseId, course.numberOfModules, course.numberOfActivities);

            results.push({
              courseName: course.courseName,
              success: true,
            });
          } else {
            results.push({
              courseName: course.courseName,
              success: false,
              error: "Failed to create course",
            });
          }
        } catch (error) {
          results.push({
            courseName: course.courseName,
            success: false,
            error: error.message || "Unknown error",
          });
        }
      }

      setUploadResults(results);
      setIsProcessing(false);
      setProcessingStatus("");

      // Check if all courses were successful
      const allSuccessful = results.every((result) => result.success);
      if (allSuccessful && results.length > 0) {
        setModalState({ state: BulkUploadModalState.UPLOAD_CONFIRMATION });
      } else {
        setModalState({ state: BulkUploadModalState.UPLOAD_ERROR });
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setIsProcessing(false);
      setProcessingStatus("");
      setUploadResults([
        {
          courseName: "File processing error",
          success: false,
          error: error.message || "Failed to process Excel file",
        },
      ]);
      setModalState({ state: BulkUploadModalState.UPLOAD_ERROR });
    }
  };

  return (
    <BulkUploadCourseModal
      visible={visible}
      isProcessing={isProcessing}
      processingStatus={processingStatus}
      selectedFile={selectedFile}
      uploadResults={uploadResults}
      isMedium={isMedium}
      onClose={handleClose}
      onFileChange={handleFileChange}
      onDownloadTemplate={handleDownloadTemplate}
      onUpload={handleUpload}
      fileInputRef={fileInputRef}
    />
  );
};

export default BulkUploadCourseModalContainer;
